/**
 * VPP (Virtual Power Plant) API
 * Real-time monitoring endpoints
 */

import { apiClient } from "./client";
import type {
  CurrentPricesResponse,
  Granularity,
  RealtimeGauge,
  Trailing24hData,
  VPPMetrics,
  ZonePricesResponse,
} from "./types";

/**
 * Get real-time gauge data for VPP dashboard
 */
export async function getRealtimeGauges(): Promise<RealtimeGauge> {
  return apiClient.get<RealtimeGauge>("/api/v1/vpp/realtime");
}

/**
 * Get current electricity prices (PUN and MGP Nord)
 * @param zone Price zone (default: NORD)
 * @param granularity PT60 for hourly or PT15 for quarter-hourly
 */
export async function getCurrentPrices(
  zone: string = "NORD",
  granularity: Granularity = "PT60"
): Promise<CurrentPricesResponse> {
  return apiClient.get<CurrentPricesResponse>(
    `/api/v1/vpp/prices/current?zone=${zone}&granularity=${granularity}`
  );
}

/**
 * Get trailing production and price data for specified number of days
 * @param zone Price zone (NORD, PUN, etc.)
 * @param plantId Optional plant ID to filter
 * @param granularity PT60 for hourly or PT15 for quarter-hourly
 * @param days Number of days to look back (1-30)
 */
export async function getTrailing24h(
  zone: string = "NORD",
  plantId?: number,
  granularity: Granularity = "PT60",
  days: number = 1
): Promise<Trailing24hData> {
  let url = `/api/v1/vpp/trailing-24h?zone=${zone}&granularity=${granularity}&days=${days}`;
  if (plantId) {
    url += `&plant_id=${plantId}`;
  }
  return apiClient.get<Trailing24hData>(url);
}

/**
 * Get VPP summary metrics
 */
export async function getVPPMetrics(): Promise<VPPMetrics> {
  return apiClient.get<VPPMetrics>("/api/v1/vpp/metrics");
}

/**
 * Combined VPP data for the VPP page
 * Fetches realtime gauges, prices, and trailing data for both NORD and PUN zones
 * @param zone Price zone (default: NORD)
 * @param granularity PT60 for hourly or PT15 for quarter-hourly
 * @param days Number of days to look back (1-30)
 */
export async function getVPPPageData(
  zone: string = "NORD",
  granularity: Granularity = "PT60",
  days: number = 1
): Promise<{
  realtime: RealtimeGauge;
  prices: CurrentPricesResponse;
  trailing24hNord: Trailing24hData;
  trailing24hPun: Trailing24hData;
}> {
  const [realtime, prices, trailing24hNord, trailing24hPun] = await Promise.all([
    getRealtimeGauges(),
    getCurrentPrices(zone, granularity),
    getTrailing24h("NORD", undefined, granularity, days),
    getTrailing24h("PUN", undefined, granularity, days),
  ]);

  return { realtime, prices, trailing24hNord, trailing24hPun };
}

/**
 * Get prices for all Italian market zones with sparklines (Task 77)
 * Returns NORD, CNOR, CSUD, SUD, SICI, SARD zone prices
 * @param granularity PT60 for hourly or PT15 for quarter-hourly (different GME dataset)
 */
export async function getZonePrices(
  granularity: Granularity = "PT60"
): Promise<ZonePricesResponse> {
  return apiClient.get<ZonePricesResponse>(
    `/api/v1/vpp/prices/zones?granularity=${granularity}`
  );
}
